/**
 * Who may make each routing decision on one review, by their position on it.
 *
 * A port of `services/routing_positions.py`, which is the authority: the server
 * refuses with these exact sentences, and this decides the same answer early so
 * a reviewer reads why a control is unavailable instead of discovering it from
 * a failed request. Keep the two in step — a rule that drifts here does not
 * weaken the guard, it misinforms the person reading it.
 */

import type { Role } from "@/lib/roles"

import type { UserId } from "./data/review-data"

export type RoutingAction = "hand_off" | "throwback" | "advance_first_pass" | "sign_off_second_pass"

export interface RoutingDecision {
  allowed: boolean
  /** Permitted, but against the holder's position — the trail records it as one. */
  positionOverride: boolean
  /** Why not, in the server's own words. Null when allowed, or when nobody is signed in. */
  refusal: string | null
}

const POOL_PICK_UP_ROLES: readonly Role[] = ["sme", "admin"]

/** Signing off the second pass is admin-only; everything else an SME may override. */
const OVERRIDE_ROLES: Record<RoutingAction, readonly Role[]> = {
  hand_off: ["sme", "admin"],
  throwback: ["sme", "admin"],
  advance_first_pass: ["sme", "admin"],
  sign_off_second_pass: ["admin"],
}

const OVERRIDE_ROLE_PHRASES: Record<RoutingAction, string> = {
  hand_off: "an SME or an admin",
  throwback: "an SME or an admin",
  advance_first_pass: "an SME or an admin",
  sign_off_second_pass: "an admin",
}

const ACTION_PHRASES: Record<RoutingAction, string> = {
  hand_off: "hand it on",
  throwback: "return it to the pool",
  advance_first_pass: "finish its first pass",
  sign_off_second_pass: "sign off its second pass",
}

/**
 * The first pass belongs to whoever holds the review, else whoever owns it,
 * else — on a review nobody holds — the person acting now, who is about to.
 */
export function firstPassReviewerId(
  review: { assigneeId: UserId | null; ownerId: UserId | null },
  actorId: UserId | null,
): UserId | null {
  return review.assigneeId ?? review.ownerId ?? actorId
}

/** Nobody reviews their own pass, so the first-pass reviewer is not offered. */
export function secondPassCandidates<Person extends { userId: UserId }>(
  assignable: readonly Person[],
  firstPassReviewer: UserId | null,
): Person[] {
  return assignable.filter((person) => person.userId !== firstPassReviewer)
}

/** The engagement's primary contact, when they are eligible to take it. */
export function defaultSecondPassReviewer<Person extends { userId: UserId }>(
  primaryClientContactId: UserId | null,
  candidates: readonly Person[],
): Person | null {
  return candidates.find((person) => person.userId === primaryClientContactId) ?? null
}

export function decideRouting(
  actor: { id: UserId; role: Role } | null,
  holderId: UserId | null,
  action: RoutingAction,
): RoutingDecision {
  // No session: refuse silently. There is nothing to explain to nobody, and a
  // refusal sentence would render before the user is known.
  if (actor === null) {
    return { allowed: false, positionOverride: false, refusal: null }
  }
  if (action === "hand_off" && holderId === null) {
    return POOL_PICK_UP_ROLES.includes(actor.role)
      ? { allowed: true, positionOverride: false, refusal: null }
      : {
          allowed: false,
          positionOverride: false,
          refusal: "Only an SME or an admin picks a review up from the pool.",
        }
  }
  if (holderId === actor.id) {
    return { allowed: true, positionOverride: false, refusal: null }
  }
  if (OVERRIDE_ROLES[action].includes(actor.role)) {
    return { allowed: true, positionOverride: true, refusal: null }
  }
  const whoMay = OVERRIDE_ROLE_PHRASES[action]
  const what = ACTION_PHRASES[action]
  return {
    allowed: false,
    positionOverride: false,
    refusal:
      holderId === null
        ? `Nobody holds this review, so only ${whoMay} may ${what}.`
        : `Only the reviewer holding this review, or ${whoMay}, may ${what}.`,
  }
}
